import type { Role } from './permissions';
import type { Invoice } from './fixtures';
import type { LoadStatus, LoadStatusOrUnknown } from '@/lib/loadStatus';
import type { DocumentKind } from '@/lib/loadDocs';

/**
 * Domain types of the fleet and broker screens (camelCase, mapped from the snake_case rows the hooks
 * read). Kept apart from data/fixtures.ts on purpose: nothing here is sample data.
 */

export const CARRIER_STATUSES = ['active', 'suspended', 'inactive'] as const;
export const OWNER_STATUSES = ['active', 'inactive'] as const;
export const TRUCK_STATUSES = ['active', 'out_of_service', 'inactive'] as const;
export const DRIVER_STATUSES = ['active', 'inactive'] as const;
export const SEAT_STATUSES = ['active', 'ended'] as const;

/** Every value a fleet chip can show (the `fleet.*` token group). */
export const FLEET_STATUSES = ['active', 'suspended', 'out_of_service', 'inactive', 'ended'] as const;
export type FleetStatus = (typeof FLEET_STATUSES)[number];

export type CarrierStatus = (typeof CARRIER_STATUSES)[number];
export type OwnerStatus = (typeof OWNER_STATUSES)[number];
export type TruckStatus = (typeof TRUCK_STATUSES)[number];
export type DriverStatus = (typeof DRIVER_STATUSES)[number];
export type SeatStatus = (typeof SEAT_STATUSES)[number];

/** broker_setup_status, in the enum's order. */
export const SETUP_STATUSES = ['not_started', 'in_progress', 'submitted', 'approved', 'rejected'] as const;
export type SetupStatus = (typeof SETUP_STATUSES)[number];

/**
 * A status outside the vocabulary falls back to `fallback` with a warn, never a crash: the server only
 * returns known values, so a miss means the portal is older than the database.
 */
export function toStatus<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  if (typeof value === 'string' && (allowed as readonly string[]).includes(value)) return value as T;
  console.warn('unknown status value, falling back', { value, fallback });
  return fallback;
}

export interface Owner {
  id: string;
  legalName: string;
  dba: string | null;
  ein: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  status: OwnerStatus;
  /** The raw row, for the edit form (field-spec keys are snake_case). */
  row: Record<string, unknown>;
}

export interface Carrier {
  id: string;
  legalName: string;
  dba: string | null;
  mcNumber: string | null;
  dotNumber: string | null;
  ownerId: string | null;
  status: CarrierStatus;
  row: Record<string, unknown>;
}

export interface Truck {
  id: string;
  carrierId: string;
  unitNumber: string;
  vin: string | null;
  year: number | null;
  make: string | null;
  model: string | null;
  plate: string | null;
  plateState: string | null;
  vehicleType: string | null;
  cargoType: string | null;
  lengthFt: number | null;
  widthFt: number | null;
  heightFt: number | null;
  ownerId: string | null;
  status: TruckStatus;
  row: Record<string, unknown>;
}

export interface Driver {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  licenseNumber: string | null;
  licenseState: string | null;
  status: DriverStatus;
  notes: string | null;
  userId: string | null;
  row: Record<string, unknown>;
}

/** A driver's seat on a carrier (`driver_carriers`). */
export interface Seat {
  carrierId: string;
  driverId: string;
  status: SeatStatus;
  startedOn: string | null;
  endedOn: string | null;
}

/** An ACTIVE truck-driver assignment. */
export interface Assignment {
  id: string;
  truckId: string;
  driverId: string;
  carrierId: string;
  startedOn: string | null;
}

/** An open app invite (`driver_invites`, explicit columns: `code_hash` is never granted). */
export interface Invite {
  id: string;
  driverId: string;
  createdAt: string;
  expiresAt: string;
}

export type InviteState =
  | { kind: 'linked' }
  | { kind: 'pending'; expiresAt: string }
  | { kind: 'expired' }
  | { kind: 'none' };

export interface Broker {
  id: string;
  name: string;
  mcNumber: string | null;
  dotNumber: string | null;
  city: string | null;
  state: string | null;
  phone: string | null;
  email: string | null;
  row: Record<string, unknown>;
}

export interface BrokerSetup {
  /** Null for a virtual row (no broker_setups row yet: not started). */
  id: string | null;
  carrierId: string;
  brokerId: string;
  status: SetupStatus;
  setupDate: string | null;
  lastVerifiedAt: string | null;
  notes: string | null;
  virtual: boolean;
}

// --- team (0074..0077) ---------------------------------------------------------------------------

/** Status of an office membership (the `member.*` token group). */
export const MEMBER_STATUSES = ['active', 'inactive'] as const;
export type MemberStatus = (typeof MEMBER_STATUSES)[number];

/** `agency_member_invites.state`, in the order the Invitations card sorts its tabs. */
export const INVITE_STATES = ['pending', 'expired', 'accepted', 'declined', 'revoked', 'superseded'] as const;
export type MemberInviteState = (typeof INVITE_STATES)[number];

/** One `agency_team` row: an office member of the current agency (inactive ones included). */
export interface Member {
  userId: string;
  role: Role;
  status: MemberStatus;
  displayName: string | null;
  email: string;
  phone: string | null;
  joinedAt: string;
  invitedBy: string | null;
  deactivatedAt: string | null;
  /** The carriers a dispatcher runs (empty for the other roles). */
  carrierIds: string[];
  isSelf: boolean;
  /** The account was deleted from the app (0086): no name and no email any more ("Deleted user"). */
  deleted: boolean;
}

/** One `agency_member_invites` row (the admin's view of an office invitation). */
export interface MemberInvite {
  inviteId: string;
  email: string;
  role: Role;
  displayName: string | null;
  locale: 'en' | 'es';
  carrierIds: string[];
  invitedBy: string | null;
  invitedByName: string | null;
  createdAt: string;
  expiresAt: string;
  state: MemberInviteState;
  sendCount: number;
  lastRequestedAt: string | null;
  lastSentAt: string | null;
  lastFailedAt: string | null;
  /** Raw provider text (Resend): admins only, collapsed (D2). */
  lastError: string | null;
}

/** One `my_member_invites()` row: an invitation addressed to the caller's verified email. */
export interface MyInvite {
  inviteId: string;
  agencyId: string;
  agencyName: string;
  role: Role;
  inviterName: string | null;
  expiresAt: string;
}

/** The caller's own `user_profiles` row. */
export interface Profile {
  userId: string;
  displayName: string;
  phone: string | null;
  locale: 'en' | 'es';
}

// --- the agency ledger (carrier invoices, 0070) -------------------------------------------------------

export const PAYMENT_METHODS = ['ach', 'zelle', 'check', 'wire', 'cash', 'card', 'other'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/** An `invoice_read()` header with the ledger columns of 0070 (a superset of the dashboard Invoice). */
export interface LedgerInvoice extends Invoice {
  paidAmount: number;
  writtenOff: number;
  balance: number;
  lastPaidOn: string | null;
  /** invoice-pdf (0146): the latest email of this invoice; null when it was never emailed. */
  emailStatus: InvoiceEmailStatus | null;
  emailedTo: string | null;
  emailedAt: string | null;
}

export type InvoiceEmailStatus = 'queued' | 'sent' | 'failed';

/** One `invoice_payments_read` row: a payment or a write-off; reversed rows stay in the history. */
export interface InvoicePayment {
  id: string;
  invoiceId: string;
  kind: 'payment' | 'write_off';
  amount: number;
  paidOn: string;
  method: PaymentMethod | null;
  reference: string | null;
  /** A write-off keeps its reason here. */
  notes: string | null;
  recordedBy: string | null;
  recordedAt: string;
  reversedAt: string | null;
  reversedBy: string | null;
  reversalReason: string | null;
}

// --- the broker ledger (0069..0073) --------------------------------------------------------------------

/** `broker_receivables_read.display_status` (the `receivable.*` token group). */
export const RECEIVABLE_DISPLAY_STATUSES = ['open', 'partially_paid', 'overdue', 'disputed', 'paid', 'short_paid'] as const;
export type ReceivableDisplayStatus = (typeof RECEIVABLE_DISPLAY_STATUSES)[number];

/** `load_receivables.status`: open until it closes paid or short paid. */
export const RECEIVABLE_STATUSES = ['open', 'paid', 'short_paid'] as const;
export type ReceivableStatus = (typeof RECEIVABLE_STATUSES)[number];

export const PAYERS = ['broker', 'factoring'] as const;
export type Payer = (typeof PAYERS)[number];

/** One receivable of the current agency: the read's row plus fee_held / notes and the derived fields. */
export interface Receivable {
  loadId: string;
  loadNumber: string;
  carrierId: string;
  carrierName: string | null;
  brokerId: string | null;
  brokerName: string | null;
  invoiceNumber: string;
  invoicedOn: string;
  dueOn: string;
  amount: number;
  received: number;
  balance: number;
  status: ReceivableStatus;
  displayStatus: ReceivableDisplayStatus;
  factored: boolean;
  factoringCompany: string | null;
  disputedAt: string | null;
  disputeReason: string | null;
  shortAmount: number | null;
  shortReason: string | null;
  closedOn: string | null;
  lastPaidOn: string | null;
  daysOutstanding: number | null;
  daysToPay: number | null;
  /** From `load_receivables` (the read omits both). */
  feeHeld: boolean;
  notes: string | null;
  /** Derived in the hook. */
  tab: 'open' | 'overdue' | 'disputed' | 'closed';
  zeroBalance: boolean;
  dispatcherUserId: string | null;
  dispatcherName: string | null;
}

/** One `broker_receivable_payments_read` row. */
export interface Receipt {
  id: string;
  loadId: string;
  amount: number;
  paidOn: string;
  payer: Payer;
  reference: string | null;
  notes: string | null;
  recordedBy: string | null;
  recordedAt: string;
  reversedAt: string | null;
  reversedBy: string | null;
  reversalReason: string | null;
}

/** A load waiting to be invoiced to its broker (POD received, or legacy invoiced with no receivable). */
export interface ReadyLoad {
  loadId: string;
  loadNumber: string;
  status: 'pod_received' | 'invoiced';
  carrierId: string;
  carrierName: string | null;
  factoringCompany: string | null;
  brokerId: string | null;
  brokerName: string | null;
  dispatcherUserId: string | null;
  dispatcherName: string | null;
  /** linehaul + accessorial: the server's default amount. */
  rate: number;
  /** Marked invoiced before receivables existed: the mark adopts it. */
  adoption: boolean;
}

// --- loads (portal-loads, design 3.1) --------------------------------------------------------------

export type StopKind = 'pickup' | 'delivery';

/** One `loads` row, camelCased. Money in dollars (numeric(12,2)); an unknown status is `unknown`. */
export interface Load {
  id: string;
  agencyId: string;
  carrierId: string;
  driverId: string | null;
  truckId: string | null;
  dispatcherUserId: string | null;
  brokerId: string | null;
  number: string;
  status: LoadStatusOrUnknown;
  commodity: string | null;
  weight: number | null;
  loadedMiles: number | null;
  deadheadMiles: number | null;
  linehaul: number | null;
  /** The sum of the four itemized extras below (the column `accessorial_amount`, kept by a trigger). */
  accessorial: number;
  /** Itemized extras (0137): TONU (only on a canceled load), detention, lumper, other. */
  tonu: number;
  detention: number;
  lumper: number;
  other: number;
  brokerReference: string | null;
  notes: string | null;
  driverRespondedAt: string | null;
  createdAt: string;
  updatedAt: string;
  isTest: boolean;
}

export interface LoadStop {
  loadId: string;
  seq: number;
  kind: StopKind;
  company: string | null;
  addressLine1: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  contactName: string | null;
  contactPhone: string | null;
  referenceNumber: string | null;
  notes: string | null;
  appointmentAt: string | null;
  arrivedAt: string | null;
  departedAt: string | null;
  /** The map pin (0087): both or neither; cleared by the trigger when the address changes. */
  latitude: number | null;
  longitude: number | null;
  addressLine2: string | null;
  /** The IANA zone the appointment was typed in; null on a stop from before it was stored (display then
   *  uses the state's zone, lib/stopZone `stopZoneOf`). */
  timeZone: string | null;
  /** The end of the appointment window (same day as `appointmentAt`); null for a fixed appointment. */
  appointmentEndAt: string | null;
}

export interface LoadEvent {
  id: string;
  loadId: string;
  kind: string;
  from: LoadStatus | null;
  to: LoadStatus | null;
  detail: Record<string, unknown>;
  /** On whose behalf (the driver's USER id for answers and road steps). */
  actorUserId: string | null;
  /** Who typed it (`auth.uid()`). */
  enteredBy: string | null;
  at: string;
}

export interface LoadDocument {
  id: string;
  loadId: string;
  kind: DocumentKind;
  storagePath: string;
  fileName: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  uploadedBy: string | null;
  createdAt: string;
  /** 0156: taken off the load by the office (the copy stays, downloadable, in the history). */
  archivedAt: string | null;
  archivedBy: string | null;
  /** The copy that replaced this one, when it was replaced (not just archived). */
  replacedBy: string | null;
  /** Why, from the office's load log (`document_archived` / `document_replaced`, detail.reason). */
  archiveReason: string | null;
}

/** The `load_money` view: `ratePerMile` per TOTAL mile (loaded + deadhead), `loadedRatePerMile` per
 *  loaded mile (0078; null at 0 loaded miles). */
export interface LoadMoney {
  ratePerMile: number | null;
  loadedRatePerMile: number | null;
  totalMiles: number;
  minimumRatePerMile: number | null;
  belowMinimumRpm: boolean | null;
}

/** The frozen fee (`load_settlement`, admin and accounting only). */
export interface LoadFee {
  pct: number;
  fee: number;
  carrierNet: number;
  earnedAt: string;
  disputedAt: string | null;
}

export const TASK_PRIORITIES = ['low', 'normal', 'high'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

export const TASK_STATUSES = ['open', 'in_progress', 'completed', 'canceled'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

/** A task (`tasks`: SQL raises the system ones, the office writes manual ones through task_* RPCs, 0116). */
export interface Task {
  id: string;
  loadId: string | null;
  carrierId: string | null;
  driverId: string | null;
  title: string;
  detail: string | null;
  priority: TaskPriority;
  status: TaskStatus;
  dueAt: string | null;
  source: string;
  createdAt: string;
  assignedUserId: string | null;
  /** Who typed a manual task; null = the system raised it. */
  createdBy: string | null;
  completedAt: string | null;
}

/** A driver the dispatch dialog offers (an ACTIVE seat on the load's carrier). */
export interface DispatchDriver {
  id: string;
  name: string;
  phone: string | null;
  /** Has an app account (answers in the app) or not (answer by phone). */
  hasApp: boolean;
  isTest: boolean;
  /** The truck of the driver's active pairing on this carrier, the dialog's prefill. */
  pairedTruckId: string | null;
}

export interface DispatchTruck {
  id: string;
  unitNumber: string;
  make: string | null;
  model: string | null;
  /** The drivers of the truck's active pairings (two on a team truck), for the "Paired with Ana, Luis" marker. */
  pairedDriverIds: string[];
}

